// ─────────────────────────────────────────────────────────────────────
// /admin — IT-Admin landing page. Shows the three admin tools as cards
// (Employee Roles, Employee EPO Limits, User Permissions).
//
// Built in Round 20 (2026) — replaces the workflow-grid "Admin · 3" card
// that used to sit on /workflows. We pulled it out of the workflows
// launcher (which is for everyone) into a dedicated page so the IT
// Admin section stays self-contained.
//
// Route guarded by <RoleProtected requireItAdmin> in allRoutes.js. The
// backend endpoints under /api/admin/* are also [Authorize(Roles="IT Admin")]
// so frontend hiding is defense-in-depth, not the security boundary.
// ─────────────────────────────────────────────────────────────────────
import React from "react";
import { Container, Row, Col } from "reactstrap";
import { useNavigate } from "react-router-dom";
import MenuCard from "../../../Components/Common/MenuCard";
import { DashboardPageHeader } from "../../../Components/Common/DashboardKit";
import useAuth from "../../Personal Attendance/useAuth";

const ADMIN_TILES = [
    {
        key: "access",
        label: "Access Management",
        subtitle: "Roles, role permissions & access preview",
        icon: "ri-shield-keyhole-line",
        tone: "danger",
        to: "/admin/access", module: "admin.access",
    },
    {
        key: "roles",
        label: "Employee Roles",
        subtitle: "Assign role per employee",
        icon: "ri-user-settings-line",
        tone: "danger",
        to: "/admin/employee-roles", module: "admin.employeeRoles",
    },
    {
        key: "epolimits",
        label: "Employee EPO Limits",
        subtitle: "Per-user EPO approval cap",
        icon: "ri-money-dollar-circle-line",
        tone: "danger",
        to: "/admin/employee-epo-limits", module: "admin.epoLimits",
    },
    {
        key: "userperms",
        label: "User Permissions",
        subtitle: "Grant individual permissions",
        icon: "ri-key-2-line",
        tone: "danger",
        to: "/admin/user-permissions", module: "admin.userPermissions",
    },
];

export default function AdminHub() {
    document.title = "Admin | SSW Technologies";
    const navigate = useNavigate();
    const auth = useAuth();
    // Show only the tiles this user can actually open (IT Admin sees all via the
    // module gate). A tile with no module is always shown.
    const tiles = ADMIN_TILES.filter(
        (t) => !t.module ||
            (typeof auth.canAccessModule === "function" && auth.canAccessModule(t.module))
    );

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-shield-keyhole-line"
                    eyebrow="Administration"
                    title="Admin"
                    subtitle="Role, EPO-limit, and per-user permission tools"
                    tone="rose"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Admin" },
                    ]}
                />

                <Row className="g-3">
                    {tiles.map((t) => (
                        <Col xxl={3} xl={4} md={6} sm={6} key={t.key}>
                            <div style={{ height: "100%" }}>
                                <MenuCard
                                    icon={t.icon}
                                    label={t.label}
                                    subtitle={t.subtitle}
                                    tone={t.tone}
                                    onClick={() => navigate(t.to)}
                                />
                            </div>
                        </Col>
                    ))}
                </Row>
            </Container>
        </div>
    );
}
